import React, { useEffect, useMemo, useRef, useState } from 'react';
import databaseAPI from '../api/databaseAPI';
import '../styles/Products.css';

import { useNavigate } from 'react-router-dom';
import { authFetch, authHeaders } from '../api/auth';

const isProductPreorder = (product) => {
  if (product?.is_preorder === true || product?.preorder_status === true) return true;
  if (!Array.isArray(product?.metafields)) return false;
  return product.metafields.some((m) => {
    const namespace = String(m?.namespace || '').toLowerCase();
    const key = String(m?.key || '').toLowerCase();
    const value = String(m?.value || '').trim().toLowerCase();
    if (namespace !== 'custom') return false;
    if (key !== 'preorder_status' && key !== 'preorder') return false;
    return ['true', '1', 'yes', 'active'].includes(value);
  });
};

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const firstImage = product.images?.[0]?.src || product.images?.[0]?.url || '';
  const price = product.variants?.[0]?.price || product.variants?.[0]?.compare_at_price || '—';
  const variantId = product.variants?.[0]?.id || product.variants?.[0]?.shopify_id || '';
  // Detect preorder via metafields array if present (namespace: custom, key: preorder_status = true)
  const isPreorder = isProductPreorder(product);
  return (
    <div className="product-card" role="button" onClick={() => navigate(`/products/${product.id || product.shopify_id}`)}>
      <div className="product-media">
        {firstImage ? <img src={firstImage} alt={product.title} /> : <div className="skeleton" style={{ width: '100%', height: '100%' }} />}
      </div>
      <div className="product-body">
        <div className="product-title">{product.title}</div>
        <div className="product-subtitle">{product.vendor || '—'} • {product.product_type || '—'}</div>

        <div className="pill-row">
          {(product.tags || '').split(',').filter(Boolean).slice(0, 3).map(t => (
            <span key={t.trim()} className="pill">{t.trim()}</span>
          ))}
          <span className={`badge ${product.status === 'active' ? 'success' : product.status ? 'warning' : 'muted'}`}>{product.status || '—'}</span>
          {isPreorder && <span className="badge warning" style={{ marginLeft: 6 }}>PREORDER</span>}
        </div>

        <div className="stats-row">
          <div className="stat">
            <div className="label">Variants</div>
            <div className="value">{product.variants?.length || 0}</div>
          </div>
          <div className="stat">
            <div className="label">Price</div>
            <div className="value">{price}</div>
          </div>
          <div className="stat">
            <div className="label">Variant ID</div>
            <div className="value">{variantId || '—'}</div>
          </div>
          <div className="stat">
            <div className="label">Updated</div>
            <div className="value">{(product.shopify_updated_at || product.updated_at || '').toString().slice(0, 10) || '—'}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Products = () => {
  const CATEGORY_TABS = [
    { id: 'all', label: 'All products' },
    { id: 'preorder', label: 'Preorder' },
    { id: 'digital', label: 'Digital download' },
    { id: 'books', label: 'Books' },
    { id: 'magazine', label: 'Magazine' },
  ];
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [nextPageInfo, setNextPageInfo] = useState(null);
  const sentinelRef = useRef(null);

  // Filters
  const [query, setQuery] = useState('');
  const [categoryTab, setCategoryTab] = useState('all');

  const loadProducts = async ({ reset = true } = {}) => {
    setLoading(true);
    setError('');
    try {
      const base = databaseAPI.baseURL || '';
      const params = new URLSearchParams();
      params.set('limit', '50');
      const url = `${base}/products/?${params.toString()}`;
      const res = await authFetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = data.products || data || [];
      const initial = Array.isArray(list) ? list : [];
      setNextPageInfo(data.next_page_info || null);
      if (reset) setProducts(initial);
      else setProducts(prev => [...prev, ...initial]);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!nextPageInfo) return;
    setLoadingMore(true);
    setError('');
    try {
      const base = databaseAPI.baseURL || '';
      const params = new URLSearchParams();
      params.set('limit', '50');
      params.set('page_info', nextPageInfo);
      const url = `${base}/products/?${params.toString()}`;
      const res = await authFetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = data.products || [];
      const pageItems = Array.isArray(list) ? list : [];
      setNextPageInfo(data.next_page_info || null);

      setProducts((prev) => [...prev, ...pageItems]);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    loadProducts({ reset: true });
  }, []);

  useEffect(() => {
    if (!sentinelRef.current) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;
        if (loading || loadingMore || !nextPageInfo) return;
        loadMore();
      },
      { root: null, rootMargin: '200px 0px', threshold: 0 }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loading, loadingMore, nextPageInfo]);

  const filtered = useMemo(() => {
    const list = products.filter(p => {
      const q = query.trim().toLowerCase();
      const matchesQ = !q || p.title?.toLowerCase().includes(q) || p.handle?.toLowerCase().includes(q) || p.vendor?.toLowerCase().includes(q);
      if (!matchesQ) return false;

      const typeLower = (p.product_type || '').toLowerCase();
      const titleLower = (p.title || '').toLowerCase();
      const variants = p.variants || [];
      const hasShippable = variants.some(v => v?.requires_shipping === true);
      const hasDigitalOnly = variants.length > 0 && variants.every(v => v?.requires_shipping === false);
      const isPreorder = isProductPreorder(p);
      const isMagazine = typeLower.includes('magazine') || titleLower.includes('magazine');
      const isBook = typeLower.includes('book') || titleLower.includes('book');

      switch (categoryTab) {
        case 'preorder':
          return isPreorder;
        case 'digital':
          return hasDigitalOnly;
        case 'books':
          return isBook;
        case 'magazine':
          return isMagazine;
        case 'all':
        default:
          return true;
      }
    });
    // Show newest first (across loaded pages).
    return list.slice().sort((a, b) => {
      const da = new Date(a.shopify_created_at || a.created_at || 0).getTime();
      const db = new Date(b.shopify_created_at || b.created_at || 0).getTime();
      return db - da;
    });
  }, [products, query, categoryTab]);

  return (
    <div className="content-container">
      {loading ? (
        <div className="products-loading-overlay" role="status" aria-live="polite" aria-label="Loading products">
          <div className="products-loading-panel">
            <div className="products-spinner" aria-hidden="true" />
            <div className="products-loading-text">Loading products…</div>
          </div>
        </div>
      ) : null}
      <div className="products-toolbar">
        <div className="controls">
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search title / vendor / handle" className="search-input" />
        <div className="products-tabs" role="tablist" aria-label="Product category tabs">
          {CATEGORY_TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={categoryTab === tab.id}
              className={`products-tab-btn${categoryTab === tab.id ? ' active' : ''}`}
              onClick={() => setCategoryTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
          <button
            className="btn"
            onClick={async () => {
              setLoading(true);
              try {
                await databaseAPI.makeAPICall('/products/sync/', 'POST');
              } catch (_e) {
                // still try to reload list from DB even if sync fails
              } finally {
                await loadProducts({ reset: true });
              }
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="products-grid" style={{ marginTop: 16 }}>
        {loading ? (
          <>
            <div
              style={{
                gridColumn: '1 / -1',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                padding: '18px 10px',
                marginBottom: 4,
                background: 'rgba(255, 255, 255, 0.7)',
                border: '1px solid rgba(0,0,0,0.04)',
                borderRadius: 12,
              }}
            >
              <span className="skeleton" style={{ width: 16, height: 16, borderRadius: '50%' }} />
              <span style={{ fontWeight: 900, color: '#111827' }}>Loading products…</span>
            </div>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="product-card">
                <div className="product-media skeleton" />
                <div className="product-body">
                  <div className="skeleton" style={{ width: '70%', height: 14, margin: '8px 0' }} />
                  <div className="skeleton" style={{ width: '40%', height: 10, margin: '6px 0 10px' }} />
                  <div className="pill-row">
                    <div className="skeleton" style={{ width: 60, height: 18, borderRadius: 999 }} />
                    <div className="skeleton" style={{ width: 50, height: 18, borderRadius: 999 }} />
                  </div>
                  <div className="stats-row">
                    <div className="stat"><div className="skeleton" style={{ width: '100%', height: 28 }} /></div>
                    <div className="stat"><div className="skeleton" style={{ width: '100%', height: 28 }} /></div>
                    <div className="stat"><div className="skeleton" style={{ width: '100%', height: 28 }} /></div>
                  </div>
                </div>
              </div>
            ))}
          </>
        ) : filtered.map(p => (
              <ProductCard key={p.id || p.shopify_id} product={p} />
            ))}
        {!loading && filtered.length === 0 && (
          <div className="info-banner">
            {categoryTab === 'preorder' && products.length > 0 && nextPageInfo
              ? 'No preorder products on loaded pages yet — keep scrolling or clear Preorder filter.'
              : 'No products found'}
          </div>
        )}
      </div>

      {!loading && (
        <div ref={sentinelRef} style={{ height: 1, width: '100%' }} aria-hidden="true" />
      )}
      {!loading && loadingMore && (
        <div style={{ display: 'flex', justifyContent: 'center', margin: '18px 0 6px', color: '#6b7280' }}>
          Loading more products...
        </div>
      )}
    </div>
  );
};

export default Products;
